import {defineStore} from "pinia";
import RegistrationApi from "@/services/api/RegistrationApi.ts";
import {type Ref, ref} from "vue";
import type Industry from "@/interfaces/Industry.ts";
import RegisteringCompanyModel from "@/models/RegisteringCompany.ts";
import type RegisteringCompany from "@/models/RegisteringCompany.ts";
import Google from "@/services/Google.ts";
import TypeGoogle from "@/services/Google.ts";
import ExternalScriptLoader from "@/services/ExternalScriptLoader.ts";
import type State from "@/models/State.ts";
import {useErrorStore} from "@/stores/error.ts";
import {REDIRECTABLE_SLIDES, SLIDES, useSlidesStore} from "@/stores/slides.ts";
import {ORIGINS} from "@/services/ConstantSerice.ts";

export const useRegistrationStore = defineStore('registration', () => {
    const apiService = new RegistrationApi();
    const defaultIndustries: Ref<String[]> = ref([]);
    const states: Ref<State[]> = ref([]);
    const industries: Ref<Industry[]> = ref([]);
    const registeringCompany: Ref<RegisteringCompany> = ref(new RegisteringCompanyModel());
    const initializing: Ref<boolean> = ref(false);
    const googleService: Ref<TypeGoogle> = ref(new Google());
    const registrationOrigin: Ref<string|null> = ref(null);
    const errorStore = useErrorStore();
    const slidStore = useSlidesStore();

    const init = async (industriesProp: Array<string>, snitcherCode: string|null, origin: string|null) => {
        // googleService.value.initialize(); //turn back on if address field needed
        ExternalScriptLoader.loadSnitcherScript(snitcherCode);
        defaultIndustries.value = industriesProp;
        registrationOrigin.value = origin;
        initializing.value = true;

        const promises = [];

        promises.push(
            apiService.init().then(resp => processInitResponse(resp))
        );

        const url = new URL(window.location.href);

        const slide = url.searchParams.get('s');

        if (slide && Object.values(REDIRECTABLE_SLIDES).includes(slide)) {
            slidStore.setInitialSlide(slide);
        }

        if (verifyingEmail(url) && registeringCompany.value.reference) {
            promises.push(
                apiService.verifyEmail(registeringCompany.value.reference).then(resp => processEmailVerificationResponse(resp))
            );
        }

        try {
            await Promise.all(promises);
            setInitialSlide()
        } catch (e) {
            processError(e);
        }

        initializing.value = false;
    }
    
    const setInitialSlide = () => {
        if (!slidStore.initialSlides.find(slide => slide === slidStore.currentSlide)) {
            return;
        }
        
        switch (registrationOrigin.value) {
            case ORIGINS.HM:
                slidStore.setInitialSlide(SLIDES.BASIC_COMPANY_DETAILS_HOW_MUCH);
                break;
            default:
                slidStore.setInitialSlide(SLIDES.BASIC_COMPANY_DETAILS);
                break;
        }
    }

    const submitSlideData = (data: any) => {
        if (!registeringCompany.value.reference) {
            data['registration_url'] = getRegistrationUrl();

            return apiService.createBuyerProspect(data);
        } else {
            return apiService.updateBuyerProspect(registeringCompany.value.reference, data);
        }
    }

    const getRegistrationUrl = () => {
        const url = new URL(window.location.href);

        return `${url.origin}${url.pathname}`;
    }

    const verifyingEmail = (url: URL) => {
        if (url.searchParams.has('token')) {
            registeringCompany.value.reference = url.searchParams.get('token');
            return true;
        }

        return false;
    }

    const processInitResponse = (resp: any) => {
        states.value = resp.data.data.states;
        industries.value = resp.data.data.industries;

        industries.value.forEach(industry => {
            industry.selected = !! defaultIndustries.value.find(i => i === industry.slug);
            industry.services.every(service => service.selected = false);
        });

        // Move selected industries to the top
        industries.value.sort((a, b) => Number(b.selected) - Number(a.selected));
    }

    const processEmailVerificationResponse = (resp: any) => {
        if (resp.data.data.status === 'verified') {
            switch (registrationOrigin.value) {
                case ORIGINS.HM:
                    slidStore.nextSlide(SLIDES.LEAD_STATES);
                    break;
                default:
                    slidStore.nextSlide(SLIDES.USER_ACCOUNT_SETUP);
            }
            
            return;
        }

        if (resp.data.data.status === 'registered') {
            slidStore.nextSlide(SLIDES.REGISTRATION_COMPLETE);
            return;
        }
    }

    const processError = (e: any) => {
        console.error(e);
        errorStore.report('Something went wrong. Please try again');
    }

    return {
        apiService,
        states,
        industries,
        registeringCompany,
        defaultIndustries,
        initializing,
        googleService,
        registrationOrigin,
        submitSlideData,
        init
    }
});